// src/App.js
import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate, BrowserRouter, useParams, Navigate } from 'react-router-dom'; // Keep BrowserRouter here
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PullToRefresh from 'react-simple-pull-to-refresh';
import Header from './components/header';
import Footer from './components/footer';
import Home from './pages/Home';
import SearchUsersPage from './pages/SearchUsersPage';
import Profile from './pages/Profile';
import Explore from './pages/Explore';
import BreakingNewsSlider from './pages/BreakingNewsSlider';
import LikedPosts from './pages/LikedPosts';
import SavedPosts from './pages/SavedPosts';
import AboutPage from './pages/AboutPage';
import AdminDashboard from './pages/AdminDashboard';
import UserProfile from './pages/UserProfile';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import QuotesCard from './components/QuotesCard';
import CommentsPage from './pages/CommentsPage';
import ViewAllPage from './pages/ViewAllPage';
import SingleQuotePage from './pages/SingleQuotePage';
import SinglePostPage from './pages/SinglePostPage';
import NewsDetailPage from './pages/NewsDetailPage';
import PostAdPage from './pages/PostAdPage';
import PrivacyPolicy from './pages/Privacy_Policy';
import TermsAndConditions from './pages/TermsAndConditions';
// import { Link } from 'react-router-dom';
// import { requestNotificationPermission } from './utils/fcm'; // <-- 3. IMPORT our function
// import AdminDashboard from './pages/AdminDashboard'; 
import './App.css';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import JobsPage from './pages/JobsPage';
import PostJobPage from './pages/PostJobPage';
import Chatbot from './components/Chatbot';
import { LocationProvider } from './contexts/LocationContext';
// import LocationHandler from './components/LocationHandler';
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { app } from './firebase-config'; // Your firebase config
// import AppWithNotifications from './components/NotificationHandler';
import NotificationHandler from './components/NotificationHandler';
import { App as CapacitorApp } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import WebviewLoader from './pages/WebviewLoader';
import WishesPage from './pages/WishesPage';
import LocalNewsViewPage from './pages/LocalNewsViewPage';
import LiveCricketPage from './pages/LiveCricketPage';
import BottomNavigation from './components/BottomNavigation';

import { AppUpdate } from '@capawesome/capacitor-app-update';
import { Capacitor } from '@capacitor/core';
// --- IMPORT API CLIENT AND SHARED CACHE ---
import apiClient from './utils/api';
import { feedCache } from './utils/feedCache';

const checkAndDownloadInBackground = async () => {
  const result = await AppUpdate.getAppUpdateInfo();
  
  if (result.updateAvailability === 2) { // Update exists
    // Start downloading in background WITHOUT a full-screen popup
    await AppUpdate.performFlexibleUpdate(); 
  }
};

// Listen for the download completion
AppUpdate.addListener('onStateChanged', (state) => {
  if (state.installStatus === 11) { // 11 = DOWNLOADED
    // NOW and ONLY now ask the user to restart
    // Since it's already downloaded, the "Update" is instant.
    if (window.confirm("New version ready! Restart to apply?")) {
      AppUpdate.completeUpdate();
    }
  }
});

// --- NEW: APP UPDATE HANDLER COMPONENT ---
function AppUpdateHandler() {
  useEffect(() => {
    const checkUpdate = async () => {
      // 1. Only run on Android/iOS
      if (Capacitor.getPlatform() === 'web') return;

      try {
        const result = await AppUpdate.getAppUpdateInfo();
        
        // updateAvailability: 2 = UPDATE_AVAILABLE
        if (result.updateAvailability === 2) {
          
          // FORCED UPDATE: This triggers a full-screen Play Store overlay
          // The user cannot dismiss this if you configure it correctly in Play Console
          await AppUpdate.performImmediateUpdate();
          
          // After immediate update, the app restarts automatically.
        }
      } catch (error) {
        console.error("Update check failed:", error);
      }
    };

    // Run check when app comes to foreground
    const listener = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) checkUpdate();
    });

    checkUpdate();

    return () => {
      listener.then(l => l.remove());
    };
  }, []);

  return null;
}

// NEW WRAPPER COMPONENT FOR DEEP LINK LISTENER
// UPDATED: Robust Deep Link Handler
function DeepLinkHandler({ setActiveQuoteId }) {
  const navigate = useNavigate();

  useEffect(() => {
    const processIncomingUrl = (urlStr) => {
      console.log("Deep Link received:", urlStr);
      
      let newsId = "";

      // Check for /post/ (common in your shared links)
      if (urlStr.includes('post/')) {
        // Extract the ID after 'post/' and remove any trailing slashes or query params
        newsId = urlStr.split('post/').pop().split(/[?#]/)[0].replace('/', '');
      } 
      // Check for /news/ (common in notifications)
      else if (urlStr.includes('news/')) {
        newsId = urlStr.split('news/').pop().split(/[?#]/)[0].replace('/', '');
      }

      if (newsId) {
        console.log("Navigating to News ID:", newsId);
        // Navigate to the news detail page internally
        navigate(`/news/${newsId}`);
      }
      // ADD THIS BLOCK FOR QUOTE DEEP LINKS
      if (urlStr.includes('quote/')) {
        const quoteId = urlStr.split('quote/').pop().split(/[?#]/)[0].replace('/', '');
        if (quoteId) {
          console.log("Setting Modal Quote ID:", quoteId);
          setActiveQuoteId(quoteId); // Instantly pops the overlay on top of the home page
        }
      }
    };

    // 1. Handle app opening from background/multitasking
    const handleAppUrlOpen = (event) => {
      processIncomingUrl(event.url);
    };

    // 2. Handle app opening from a "Killed" state (Cold Start)
    const checkLaunchUrl = async () => {
      const { url } = await CapacitorApp.getLaunchUrl() || {};
      if (url) {
        processIncomingUrl(url);
      }
    };

    const listener = CapacitorApp.addListener('appUrlOpen', handleAppUrlOpen);
    checkLaunchUrl();

    return () => {
      listener.then(handler => handler.remove());
    };
  }, [navigate]);

  return null;
}

// --- NEW: WEB QUOTE REDIRECT HANDLER ---
// Attempts to force open the native app on mobile browser view via custom scheme
function QuoteRedirectHandler() {
  const { quoteId } = useParams();

  useEffect(() => {
    const triggerAppOpen = () => {
      window.location.href = `ainews://quote/${quoteId}`;
    };
    
    // Check if the user is loading the website on a mobile device
    if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      triggerAppOpen();
    }
  }, [quoteId]);

  return <Navigate to={`/?openQuote=${quoteId}`} replace />;
}

function App() {
  const [appIsReady, setAppIsReady] = useState(false);
  const [activeCommentPostId, setActiveCommentPostId] = useState(null);
  // 1. ADD THIS STATE FOR THE HOME OVERLAY
  const [activeQuoteId, setActiveQuoteId] = useState(null); 
  const [refreshKey, setRefreshKey] = useState(0);
  // State to manage toggle of headers & bottom bar
  const [showUi, setShowUi] = useState(true);
  // 2. CHECK URL FOR WEB FALLBACKS ON INITIAL MOUNT
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const quoteParam = params.get('openQuote');
    if (quoteParam) {
      setActiveQuoteId(quoteParam);
    }
  }, []);
  // 2. Define the refresh function
  const handleRefresh = () => {
    setRefreshKey(prevKey => prevKey + 1);
      return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, 1500); // Spinner will rotate for at least 1.5 seconds
    });
  };

  // Listen for global taps to toggle headers/footers
  useEffect(() => {
    const handleGlobalClick = (e) => {
      // Avoid toggling outer navigation when interacting with forms, buttons, dropdowns, or actions
      if (
        e.target.closest('button') || 
        e.target.closest('input') || 
        e.target.closest('select') || 
        e.target.closest('textarea') ||
        e.target.closest('a') ||
        e.target.closest('.action-item') ||
        e.target.closest('.action-button') ||
        e.target.closest('.report-modal-popover') ||
        e.target.closest('.portal-content') ||
        e.target.closest('.view-comments-link') ||
        e.target.closest('.filter-sheet-overlay') ||
        e.target.closest('.select__control') ||
        e.target.closest('.comments-overlay') || 
        e.target.closest('.chatbot-container')
      ) {
        return;
      }
      
      // Toggle outer UI when tapping standard news card spaces
      if (
    e.target.closest('.news-card') ||
    e.target.closest('.swiper-slide') ||
    e.target.closest('.cube-slide') ||
    e.target.closest('.news-card-container')
) {
    setShowUi(prev => !prev);
}
    };

    document.addEventListener('click', handleGlobalClick);
    return () => document.removeEventListener('click', handleGlobalClick);
  }, []);

  useEffect(() => {
    if (showUi) {
      document.body.classList.remove('ui-hidden');
    } else {
      document.body.classList.add('ui-hidden');
    }
  }, [showUi]);

  // Define a custom rotating spinner (SVG)
  // const LoadingSpinner = (
  //   <div className="refresh-spinner-container">
  //      <svg className="refresh-spinner" viewBox="0 0 50 50">
  //       <circle className="path" cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
  //     </svg>
  //   </div>
  // );

  useEffect(() => {
  // Listen for the custom "openComments" event triggered by the notification listener
  const handleOpenComments = (event) => {
    const postId = event.detail;
    if (postId) {
      setActiveCommentPostId(postId); // 💡 This opens your Modal!
    }
  };

  window.addEventListener('openComments', handleOpenComments);
  return () => window.removeEventListener('openComments', handleOpenComments);
}, []);
// --- PRELOAD FEED & MEDIA WHILE SPLASH IS VISIBLE ---
  const preloadInitialFeed = async () => {
    try {
      const params = { limit: 10 };
      const newsPromise = apiClient.get('/user-news', { params });
      const adsPromise = apiClient.get('/ads', { params }).catch(() => ({ data: [] }));

      const [newsResponse, adsResponse] = await Promise.all([newsPromise, adsPromise]);
      const rawNews = newsResponse.data;

      if (rawNews && rawNews.length > 0) {
        const processed = rawNews.map(item => ({
          ...item,
          timestamp: item.timestamp?._seconds 
            ? new Date(item.timestamp._seconds * 1000) 
            : new Date(item.timestamp || Date.now())
        }));

        // Instantly trigger preloading on the first 3 images/videos
        processed.slice(0, 3).forEach(item => {
          const mediaUrl = item.image || item.imageUrl;
          if (mediaUrl) {
            const img = new Image();
            img.src = mediaUrl;
          }
          const videoUrl = item.video || item.videoUrl;
          if (videoUrl) {
            const video = document.createElement('video');
            video.src = videoUrl;
            video.preload = 'auto';
          }
        });

        // Store in our unified cache
        feedCache.userNewsContent = processed;
        feedCache.lastTimestamp = processed[processed.length - 1].timestamp;
        feedCache.hasMore = rawNews.length >= 10;
      }
    } catch (error) {
      console.error("Feed preloading failed:", error);
    }
  };
   useEffect(() => {
    const initializeApp = async () => {
      // 3.5 Second maximum failsafe timer to bypass splash hanging issues
      const failsafeTimer = setTimeout(() => {
        console.warn("Failsafe: Splash screen hide timed out. Forcing app display.");
        setAppIsReady(true);
      }, 3500);

      try {
        // Run parallel preloading so items are fetched before the UI is revealed
        await preloadInitialFeed();

        if (window.Capacitor) {
          await SplashScreen.hide({ fadeOutDuration: 300 });
        }
      } catch (e) {
        console.error("Initialization error:", e);
      } finally {
        clearTimeout(failsafeTimer);
        setAppIsReady(true);
      }
    };

    initializeApp();
  }, []);
  
  // Conditionally render the app only when ready
  if (!appIsReady) {
      // You can return a simple loading screen here if needed, but usually,
      // the native splash screen is responsible for the waiting time.
      return null;
  }
  // Define the QuoteModal helper component at the bottom of src/App.js
function QuoteModal({ quoteId, onClose }) {
  const [quoteItem, setQuoteItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const db = getFirestore(app);

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const docRef = doc(db, 'quotes', quoteId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setQuoteItem({ id: docSnap.id, ...docSnap.data() });
        }
      } catch (err) {
        console.error("Error fetching modal quote:", err);
      } finally {
        setLoading(false);
      }
    };
    if (quoteId) fetchQuote();
  }, [quoteId]);

  return (
    <div className="comments-overlay" onClick={onClose} style={{ zIndex: 1100 }}>
      <div className="comments-container" onClick={(e) => e.stopPropagation()} style={{ height: 'auto', maxHeight: '85vh', padding: '16px' }}>
        <div className="comments-header">
          <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Daily Quote</h3>
          <button className="close-comments-btn" onClick={onClose} style={{ fontSize: '24px' }}>&times;</button>
        </div>
        <div style={{ marginTop: '15px', borderRadius: '12px', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '30px', color: '#666' }}>Loading Quote...</div>
          ) : quoteItem ? (
            <QuotesCard quotes={[quoteItem]} isActive={true} />
          ) : (
            <div style={{ textAlign: 'center', padding: '30px', color: '#666' }}>Quote not found</div>
          )}
        </div>
      </div>
    </div>
  );
}
  return (
    <BrowserRouter>
      <AuthProvider>
        <LocationProvider> {/* <-- WRAP WITH LOCATION PROVIDER */}
        {/* <NotificationHandler /> <-- 4. ADD THE HANDLER COMPONENT HERE */}
        {/* <AppWithNotifications />  */}
        <AppUpdateHandler />
        <NotificationHandler />
        <DeepLinkHandler setActiveQuoteId={setActiveQuoteId} />  
        <div className="app-container"> {/* Space for Bottom Bar */}                       
        {/* <NativeNotificationHandler /> */}
          <div key={refreshKey}>                                                         
        <Header />
        {/* 💡 NEW: Header reload button. Transitions with the top bar */}
              <button className="header-refresh-btn" onClick={handleRefresh} title="Refresh News">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '22px', height: '22px' }}>
                  <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
                </svg>
              </button>
        <main>
          <Routes>
            {/* <Route path="/" element={<Home />} /> */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route 
              path="/news/:newsId" 
              element={<NewsDetailPage setActiveCommentPostId={setActiveCommentPostId} />} 
            />
            <Route path="/explore" element={<Explore />} />
            <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
            <Route path="/liked" element={<PrivateRoute><LikedPosts /></PrivateRoute>} />
            <Route path="/" element={<Home setActiveCommentPostId={setActiveCommentPostId} />} />
            {/* Web fallback quote path redirected smoothly to avoid browser render issues */}
                  <Route path="/quote/:quoteId" element={<SingleQuotePage />} />
            <Route path="/saved" element={<PrivateRoute><SavedPosts /></PrivateRoute>} />
            <Route path="/posts/LocalNews" element={<LocalNewsViewPage setActiveCommentPostId={setActiveCommentPostId} />} /> 
            <Route path="/posts/:category" element={<ViewAllPage setActiveCommentPostId={setActiveCommentPostId} />} />
            <Route 
              path="/post/:postId" 
              element={<SinglePostPage setActiveCommentPostId={setActiveCommentPostId} />} 
            />
            <Route 
              path="/_deep_link/:postId" 
              element={<SinglePostPage setActiveCommentPostId={setActiveCommentPostId} />} 
            />
            <Route 
              path="/posts/Wishes" 
              element={<WishesPage setActiveCommentPostId={setActiveCommentPostId} />} 
            />
            {/* Add this near the other /profile and /news routes */}
            <Route 
              path="/user/:userId" 
              element={<UserProfile setActiveCommentPostId={setActiveCommentPostId} />} 
            />
            <Route path="/search-users" element={<PrivateRoute><SearchUsersPage /></PrivateRoute>} />
            <Route path="/webview-loader" element={<WebviewLoader />} />
            {/* THIS ROUTE IS NOW CORRECT BECAUSE THE IMPORT IS FIXED */}
            <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/post-ad" element={<PostAdPage />} />
            <Route path="/post-a-job" element={<PrivateRoute><PostJobPage /></PrivateRoute>} />
            <Route path="/posts/TopNews" element={<BreakingNewsSlider />} />
            {/* <Route path="/post/:postId/comments" element={<PrivateRoute><CommentsPage /></PrivateRoute>} /> */}
            <Route path="/live-cricket" element={<LiveCricketPage />} />
          </Routes>
        </main>
        <Footer />
        </div>
        {/* <Chatbot /> */}
        <BottomNavigation />
        {/* 3. THE COMMENTS OVERLAY (CHATBOT STYLE) */}
        {activeCommentPostId && (
          <CommentsPage 
            postId={activeCommentPostId} 
            onClose={() => setActiveCommentPostId(null)} 
          />
        )}
        {/* 4. THE NEW QUOTE OVERLAY */}
        {activeQuoteId && (
          <QuoteModal 
            quoteId={activeQuoteId} 
            onClose={() => setActiveQuoteId(null)} 
          />
        )}
        </div>
        </LocationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;